import type { TodayCampaign } from './todayData';
import { addDays, calendarDate, campaignRange, campaignStart, shortDate } from '../reach/reach';
import { BarState, CampaignPaint, campaignHue, campaignPaint } from '../reach/campaignColors';
import { neverApproved, phaseOf } from '../reach/campaignPhase';

/**
 * Today → Campaigns ("TinyCMS UX optimization review", App Prototype): the
 * next eight weeks from this week's Monday, one row per campaign that isn't
 * retired, including one whose dates passed this week. Kept apart from the component so tests can load it.
 */

export const TIMELINE_WEEKS = 8;

/** This week's Monday, local midnight, to eight weeks on. */
export function timelineWindow(now = new Date()) {
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  from.setDate(from.getDate() - ((from.getDay() + 6) % 7));
  const to = new Date(from);
  to.setDate(to.getDate() + TIMELINE_WEEKS * 7);
  return { from, to };
}

/**
 * on: running; wait: waiting on the person; sched: approved, dated; off: paused;
 * draft: set up, not scheduled; ended: its dates passed; expired: they passed
 * without an approval.
 */
export type TimelineTone = 'on' | 'wait' | 'sched' | 'off' | 'draft' | 'ended' | 'expired';

export interface TimelineRow {
  id: string;
  name: string;
  sub: string;
  color: string;
  tone: TimelineTone;
  /** Always on: one bar across the whole timeline. */
  always: boolean;
  /** Where the bar starts and ends, 0–1 across the timeline. */
  start: number;
  end: number;
  /** "Oct 15 – Nov 1" beside the bar (dated campaigns). */
  range: string;
  /** Began before the window / runs past it: the bar is cut there. */
  before?: boolean;
  after?: boolean;
  /** A dated campaign's bar and name square, in its color (campaignColors.ts). */
  paint?: CampaignPaint;
}

/** How a dated campaign's bar shows its status, in its color. */
const BAR: Partial<Record<TimelineTone, BarState>> = {
  sched: 'solid',
  wait: 'waiting',
  draft: 'draft',
  off: 'ended',
  ended: 'ended',
  expired: 'expired',
};

const COLOR: Record<TimelineTone, string> = {
  on: 'var(--ink-green-ink)',
  wait: 'var(--ink-amber)',
  sched: 'var(--ink-accent)',
  off: 'var(--ink-ink3)',
  draft: 'var(--ink-ink3)',
  ended: 'var(--ink-ink3)',
  expired: 'var(--ink-ink3)',
};

/**
 * The timeline's rows: always-on campaigns first, then dated ones by start,
 * leaving out finished campaigns and dated ones outside the window. `waiting`
 * holds the campaigns Today lists as waiting on approval.
 */
export function timelineRows(
  all: TodayCampaign[],
  waiting: Set<string>,
  win: { from: Date; to: Date },
  now = new Date()
): TimelineRow[] {
  const from = win.from.getTime();
  const span = win.to.getTime() - from;
  const frac = (t: number) => Math.min(1, Math.max(0, (t - from) / span));
  const always: TimelineRow[] = [];
  const dated: Array<TimelineRow & { at: number }> = [];

  for (const c of all) {
    if (c.status === 'completed') continue;
    const base = { id: c.id, name: c.name, start: 0, end: 1, range: '' };
    if (c.kind === 'always_on') {
      const tone: TimelineTone =
        c.status === 'active' ? 'on' : c.status === 'planning' ? 'wait' : 'off';
      const sub =
        tone === 'on'
          ? 'Always on · posting new articles'
          : tone === 'wait'
            ? 'Always on · waiting for you to turn it on'
            : 'Always on · paused';
      always.push({ ...base, sub, tone, color: COLOR[tone], always: true });
      continue;
    }

    const start = campaignStart(c);
    if (!start) continue;
    const lastDay = calendarDate(c.deadline);
    // The end date is a whole day: the bar runs to the end of it.
    const end = lastDay ? addDays(lastDay, 1).getTime() : win.to.getTime();
    if (end <= from || start.getTime() >= win.to.getTime()) continue;

    const future = start.getTime() > now.getTime();
    let tone: TimelineTone;
    let sub: string;
    // Ended comes first, as on the Campaigns page (campaignPhase.ts), so the
    // two can't disagree (backlog c175c89d).
    if (phaseOf(c, now) === 'ended') {
      tone = neverApproved(c) ? 'expired' : 'ended';
      sub = tone === 'expired' ? 'Ended · never approved' : 'Ended';
    } else if (waiting.has(c.id)) {
      tone = 'wait';
      sub = future ? `Approve by ${shortDate(start, now)}` : 'Waiting for your approval';
    } else if (c.status === 'paused') {
      tone = 'off';
      sub = 'Paused';
    } else if (c.status === 'planning') {
      // Set up but not scheduled yet: a dashed bar (Prototype).
      tone = 'draft';
      sub = 'Draft · not scheduled';
    } else {
      tone = 'sched';
      sub = future
        ? `Scheduled · starts ${shortDate(start, now)}`
        : lastDay
          ? `Running · ends ${shortDate(lastDay, now)}`
          : 'Running';
    }
    dated.push({
      ...base,
      at: start.getTime(),
      sub,
      tone,
      color: COLOR[tone],
      always: false,
      start: frac(start.getTime()),
      end: frac(end),
      range: campaignRange(c, now),
      before: start.getTime() < from,
      after: end > win.to.getTime(),
      paint: campaignPaint(campaignHue(c), BAR[tone] || 'solid'),
    });
  }

  return [...always, ...dated.sort((a, b) => a.at - b.at).map(({ at, ...r }) => r)];
}
